import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Card,
  CardContent,
  Box,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import {
  TextV2,
  ColourScoreBar,
  Switch,
  ButtonV2,
  SvgPencilWrite2Regular,
  SvgAddRegular,
} from '@insights-ltd/design-library/components';
import {
  ProfileType as ColourProfileType,
  formatDateFromIsoString,
} from '@insights-ltd/design-library/utils';
import { ProfileResponse } from 'api';
import DistributionMethodDialog from 'components/DistributionMethodDialog';
import PermissionGuard from 'components/PermissionGuard';
import EmailProgressDialog from 'components/EmailProgressDialog';
import EditContributorsDialog from 'components/EditContributorsDialog';
import RequestErrorModal from 'components/RequestErrorDialog/RequestErrorModal';
import ProfileArchiveDialog from 'components/ProfileArchiveDialog';
import { getTheme, spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  getPronounIfSupportedByProfile,
  isApiProfile,
  isExploreProfile,
} from 'domain/profile';
import Grid from '@mui/material/Grid';
import { AllDialectOptions } from 'types/dialects';
import { getDownloadDialect } from 'domain/dialect';
import DownloadProgressDialog from 'components/DownloadProgressDialog/DownloadProgressDialog';
import { CHAPTER_TYPES } from 'variables';
import { Chapters } from 'types/types';
import { PreferredDialectAndPronoun } from 'pages/Learner/helper';
import { PurchaseProfilesLocationState } from 'pages/Learner/learnerHelper';
import { sortChapters } from 'utils/mappers/sorting';
import { formatList } from 'utils/formatList';
import { useBetaEnabled } from 'features';
import ApiProfileInfoLink from './ApiProfileInfoLink';
import {
  getIsDiscoveryProfile,
  getIsProfileShareable,
  getProfileDates,
  getProfileTitleKey,
} from './profileCard.helper';
import ProfileDates from './ProfileDates';
import ProfileCardActions from './ProfileCardActions';
import ProfileCardHeader from './ProfileCardHeader';

import {
  useProfileArchive,
  useProfileContributors,
  useProfileDiscoveryShareDate,
  useProfileDownload,
  useProfileEmail,
} from './hooks';

type ProfileCardProps = {
  profile: ProfileResponse;
  learnerId: string;
  disabled?: boolean;
  selectedDialect: AllDialectOptions;
  dialectString?: string;
  onProfileDownloadError: () => void;
  organisationId: string;
  lastProfile?: boolean;
};

const ProfileCard = ({
  profile,
  learnerId,
  disabled = false,
  selectedDialect,
  dialectString,
  onProfileDownloadError,
  organisationId,
}: ProfileCardProps) => {
  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;
  const localTheme = useTheme();
  const insightsTheme = getTheme();
  const navigate = useNavigate();

  const isDFCProfile = profile.type === 'DFC_PROFILE';
  const disabledDFCProfileTile = isDFCProfile && profile.contributorCount === 0;
  const hasContributors = (profile?.contributorCount ?? 0) > 0;

  const downloadDialect = getDownloadDialect(selectedDialect, profile.dialect);

  const {
    editContributorsOpen,
    setEditContributorsOpen,
    editContributorsList,
    onContributorCheckChange,
    onContributorEditCancel,
    onHandleRemoveProfileContributors,
    removeProfileContributorsStatus,
    errorModalOpen,
    closeError,
    anonymisationType,
    handleAnonymisationSwitchChange,
  } = useProfileContributors({
    learnerId,
    profileId: profile.id,
    isDFCProfile,
  });

  const { onDownloadProfile, showDownloadProgress, setShowDownloadProgress } =
    useProfileDownload({
      learnerId,
      profileId: profile.id,
      downloadDialect,
      anonymisationType,
      onDownloadError: onProfileDownloadError,
    });

  const {
    onEmailProfile,
    handleSendEmail,
    onExploreEmailProfile,
    showEmailProgress,
    setShowEmailProgress,
    showDistOptions,
    setShowDistOptions,
  } = useProfileEmail({
    learnerId,
    profileId: profile.id,
    downloadDialect,
    anonymisationType,
    onEmailError: onProfileDownloadError,
  });

  const {
    showArchiveDialog,
    setShowArchiveDialog,
    archiveError,
    archiveReason,
    setArchiveReason,
    archiveReasonOther,
    setArchiveReasonOther,
    handleArchiveProfile,
    handleProfileArchiveClose,
  } = useProfileArchive({ learnerId, profileId: profile.id, organisationId });

  const profileTitle = t(getProfileTitleKey(profile));
  const getChapterNames = (chapters?: Chapters[]): string => {
    if (!chapters || chapters.length === 0) {
      return '';
    }
    return formatList(
      [...chapters]
        .sort(sortChapters)
        .map((chapter: Chapters) => t(CHAPTER_TYPES[chapter] ?? ''))
        .filter(Boolean),
      locale,
    );
  };

  const isDiscoveryProfile = getIsDiscoveryProfile(profile.type);

  const discoveryShareDateBetaEnabled = useBetaEnabled('discovery-share-date');

  const { lastSharedAt } = useProfileDiscoveryShareDate({
    profileId: profile.id,
    enabled: isDiscoveryProfile && discoveryShareDateBetaEnabled,
  });

  const isApiProfileType = isApiProfile(profile);

  const isProfileShareable = getIsProfileShareable(
    profile,
    disabledDFCProfileTile,
  );

  const isColourScoreBarSupported = (
    type: string,
  ): type is ColourProfileType => {
    return [
      'DISCOVERY_PROFILE',
      'DISCOVERY_PERSONAL_PROFILE',
      'IDTL_PROFILE',
      'DFC_PROFILE',
      'EXPLORE_PROFILE',
      'API_PROFILE',
      'IMPACT_PROFILE',
    ].includes(type);
  };

  const navigateToPurchaseProfiles = () => {
    const state: PurchaseProfilesLocationState = {
      evaluationId: profile.evaluation.id,
    };
    navigate('purchase-profiles', { state });
  };

  const isDisabledTile = disabled || disabledDFCProfileTile;

  const { completionDate, createdDate } = getProfileDates(profile, locale);

  const showDFCContributors = isDFCProfile && hasContributors;

  return (
    <>
      <Card
        elevation={0}
        sx={(theme) => ({
          display: 'flex',
          height: '100%',
          border: `1px solid ${theme.palette.grey[500]}`,
          ...(isDisabledTile && {
            backgroundColor: theme.palette.grey[200],
            borderColor: 'transparent',
            '& * p': { color: theme.palette.grey[700] },
          }),
        })}
      >
        <CardContent
          sx={{ p: 0, display: 'flex', flexDirection: 'column', flexGrow: 1 }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              marginBottom: (theme) => theme.spacing(spacingSizeMap.XS),
            }}
          >
            <ProfileCardHeader
              profileTitle={profileTitle}
              actionText={t('ui.event-management.learner.profiles.archive')}
              actionColor={localTheme.palette.error.main}
              onActionClick={() => setShowArchiveDialog(true)}
            >
              <ProfileDates
                completionDate={completionDate}
                createdDate={createdDate}
                showPurchaseDate={!isApiProfileType}
              />
            </ProfileCardHeader>
          </Box>
          <Divider
            sx={(theme) => ({ border: `1px solid ${theme.palette.grey[400]}` })}
          />
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <PreferredDialectAndPronoun
                pronoun={getPronounIfSupportedByProfile(profile)}
                preferredProfileDialect={profile.dialect}
              />
              {isDiscoveryProfile && (
                <>
                  <Box
                    sx={{
                      marginTop: 1,
                    }}
                  >
                    <Trans
                      i18nKey="ui.event-management.profiles.evaluator-chapters-list"
                      values={{
                        chaptersList: getChapterNames(profile.chapters),
                      }}
                      components={{
                        bold: <TextV2 variant="bodyBold" component="span" />,
                      }}
                    />
                  </Box>
                  <PermissionGuard
                    requiredPermissions={[
                      { action: 'PurchaseProfiles', scope: 'Local' },
                    ]}
                  >
                    <ButtonV2
                      buttonText={t(
                        'ui.event-management.profiles.discovery-personal-profile.purchase-additional-content.link',
                      )}
                      variant="text"
                      sx={{
                        padding: 0.5,
                        alignSelf: 'flex-start',
                      }}
                      disabled={disabled}
                      onClick={navigateToPurchaseProfiles}
                      startIcon={
                        <SvgAddRegular stroke="currentColor" width="1.2rem" />
                      }
                    />
                  </PermissionGuard>
                  {lastSharedAt && discoveryShareDateBetaEnabled && (
                    <Box
                      sx={{
                        marginTop: 1,
                      }}
                    >
                      <TextV2 variant="bodyBold">
                        {t(
                          'ui.event-management.profiles.digital-profile-shared',
                          {
                            date: formatDateFromIsoString(lastSharedAt, locale),
                          },
                        )}
                      </TextV2>
                    </Box>
                  )}
                </>
              )}
              {showDFCContributors && (
                <Box
                  sx={{
                    marginTop: 1,
                  }}
                >
                  {t(
                    'ui.event-management.profiles.evaluator-discovery-full-circle-contributors',
                    { count: profile.contributorCount ?? 0 },
                  )}
                  <PermissionGuard
                    requiredPermissions={[
                      { action: 'Learner_Update', scope: 'Global' },
                    ]}
                  >
                    <ButtonV2
                      buttonText={t(
                        'ui.event-management.learners.profile.dfc.edite-contributors.hyperlink.text',
                      )}
                      variant="text"
                      sx={{ padding: 0.5, marginLeft: -1 }}
                      onClick={() => setEditContributorsOpen(true)}
                      startIcon={
                        <SvgPencilWrite2Regular
                          stroke={insightsTheme.brandColours.darkBlue}
                          width="1.2rem"
                        />
                      }
                    />
                  </PermissionGuard>
                </Box>
              )}
              {disabledDFCProfileTile && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    marginTop: 1,
                  }}
                >
                  <TextV2 variant="bodyBold">
                    {t(
                      'ui.event-management.learner.profiles.zero-contributors',
                    )}
                  </TextV2>
                  <TextV2>
                    {t(
                      'ui.event-management.learner.profiles.profile-not-available',
                    )}
                  </TextV2>
                </Grid>
              )}
              {isApiProfileType && (
                <TextV2
                  variant="body1"
                  color="textSecondary"
                  sx={{ marginTop: 0.5 }}
                >
                  {t('ui.event-management.profiles.api-profile-info-message')}
                </TextV2>
              )}
            </Box>
            <Box
              sx={{
                marginTop: 'auto',
                paddingTop: (theme) => theme.spacing(spacingSizeMap.S),
              }}
            >
              {!isDFCProfile && isColourScoreBarSupported(profile.type) && (
                <Box
                  sx={{
                    marginBottom: !isApiProfileType ? 0 : '20px',
                  }}
                >
                  <ColourScoreBar
                    scores={profile.colourScores.conscious}
                    hideZeroScoreBar={false}
                    experienceType={profile.type}
                    labels={{
                      red: t(
                        'ui.event-management.learner.profiles.order.preference.red',
                      ),
                      green: t(
                        'ui.event-management.learner.profiles.order.preference.green',
                      ),
                      blue: t(
                        'ui.event-management.learner.profiles.order.preference.blue',
                      ),
                      yellow: t(
                        'ui.event-management.learner.profiles.order.preference.yellow',
                      ),
                    }}
                  />
                </Box>
              )}
              <Box
                sx={{ display: 'flex', flexDirection: 'column', marginTop: 2 }}
              >
                {showDFCContributors && (
                  <Grid sx={{ marginLeft: 0.5 }}>
                    <Switch
                      checked={anonymisationType === 'ANONYMISED'}
                      onChange={handleAnonymisationSwitchChange}
                      labelLeft={t(
                        'ui-event-management.events.select-contributor-option-named',
                      )}
                      labelRight={t(
                        'ui-event-management.events.select-contributor-option-anonymised',
                      )}
                      disabled={disabled}
                      data-testid="anonymisation-switch"
                    />
                  </Grid>
                )}
                {isExploreProfile(profile) && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      onExploreEmailProfile('share-digital-profile-link')
                    }
                    disabled={!!disabled}
                    sx={(theme) => ({
                      borderColor: disabled
                        ? `${theme.palette.grey[600]} !important`
                        : 'undefined',
                      marginTop: theme.spacing(spacingSizeMap.S),
                    })}
                  >
                    {t(
                      'ui.event-management.learner.profiles.share-profile-link',
                    )}
                  </Button>
                )}
                {isProfileShareable && (
                  <ProfileCardActions
                    onShare={onEmailProfile}
                    onDownload={onDownloadProfile}
                    disabled={disabled}
                  />
                )}
              </Box>
              {isApiProfileType && <ApiProfileInfoLink />}
            </Box>
          </Box>
        </CardContent>
      </Card>
      <DownloadProgressDialog
        open={showDownloadProgress}
        dialect={dialectString}
        onClose={() => setShowDownloadProgress(false)}
      />
      <EmailProgressDialog
        singleProfile
        open={showEmailProgress}
        dialect={dialectString}
        onClose={() => setShowEmailProgress(false)}
      />
      <DistributionMethodDialog
        open={showDistOptions}
        profileCount={1}
        onSendViaLink={handleSendEmail}
        onSendViaEmail={() => handleSendEmail('attachment')}
        onCancel={() => setShowDistOptions(false)}
        showDigitalProfileLink={isDiscoveryProfile}
      />
      <EditContributorsDialog
        open={editContributorsOpen}
        onCancel={onContributorEditCancel}
        onCheckChange={onContributorCheckChange}
        selectedContributorIds={editContributorsList}
        contributors={profile.contributors || []}
        onHandleRemoveProfileContributors={onHandleRemoveProfileContributors}
        isPending={removeProfileContributorsStatus === 'pending'}
      />
      <RequestErrorModal open={errorModalOpen} onClose={closeError} />
      {showArchiveDialog && (
        <ProfileArchiveDialog
          open={showArchiveDialog}
          profileTitle={profileTitle}
          onCancel={handleProfileArchiveClose}
          archiveReason={archiveReason}
          setArchiveReason={setArchiveReason}
          archiveReasonOther={archiveReasonOther}
          setArchiveReasonOther={setArchiveReasonOther}
          onSubmit={handleArchiveProfile}
          archiveError={archiveError}
        />
      )}
    </>
  );
};

export default ProfileCard;
